<template>
	<div class="user-account-page">
		<div class="user-account-content desktop-center">
			<div class="sk-widget-title">
				Details
				<button
					class="button button-auto button-secondary-dark"
					@click="editUserAccountClicked"
				>
					Edit
				</button>
			</div>
			<div class="sk-widget">
				<div class="sk-widget-user-details">
					<div class="user-details-name">
						{{ user.username }}
					</div>
					<div v-if="hasApartmentNumber">
						Unit {{ user.apartmentNumber }}
					</div>
					<div>
						{{ user.phone | phoneNumber }}
					</div>
					<div>
						{{ user.email }}
					</div>
				</div>
			</div>
			<div class="sk-widget-title">
				Payment
				<button
					class="button button-auto button-secondary-dark"
					@click="newCreditCardClicked"
				>
					<i class="sk-icon-plus"></i>
					New Card
				</button>
			</div>
			<div class="sk-widget">
				<div
					class="sk-widget-line-item"
					v-for="card in creditCards"
					:key="card.id"
				>
					<div class="card-details">
						<div>
							{{ card.card_brand }} ...
							{{ card.card_last_4_digits }}
						</div>
						<div>
							EXP {{ card.card_exp_month }}/{{
								card.card_exp_year
							}}
						</div>
					</div>
					<div class="card-actions">
						<div class="sk-radio-group">
							<SkRadio
								:option="card.id"
								v-model="defaultCardId"
							/>
							<div>Default</div>
						</div>
						<button @click="deleteCardClicked(card)">Delete</button>
					</div>
				</div>
			</div>
			<div class="sk-widget-title">Notifications</div>
			<div class="sk-widget">
				<div class="sk-widget-line-item">
					<div>
						SMS Daily Menus
						<span class="button button-green button-auto">
							5% off
						</span>
					</div>
					<div>
						<SkToggle v-model="optedIntoDailySMS" />
					</div>
				</div>
			</div>
			<div class="sk-widget">
				<div class="sk-widget-line-item">
					<div>Upcoming Event Reminders</div>
					<div>
						<SkToggle v-model="optedIntoEventReminders" />
					</div>
				</div>
			</div>
		</div>
		<NewCreditCardModal ref="newCreditCardModal" />
		<EditUserAccountModal ref="editUserAccountModal" />
	</div>
</template>

<style scoped>
.user-account-page {
	background: var(--sk-grey-bg);
	overflow-y: auto;
}

.user-account-content {
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 10px 20px 0 20px;
}

.card-details div:first-child {
	font-size: 12px;
	color: var(--gR-base-80);
	font-weight: 600;
}

.card-details div:last-child {
	font-size: 10px;
	color: var(--sk-grey2);
}

.card-actions {
	display: flex;
	align-items: center;
}

.card-actions > button {
	color: var(--sk-grey3);
	background: none;
}

.sk-radio-group div {
	color: var(--gR-base-90);
}

.card-actions > button,
.sk-radio-group div {
	font-size: 12px;
	cursor: pointer;
}

.sk-radio-group {
	margin-right: 40px;
}

.sk-radio-group >>> .sk-radio {
	width: 15px;
	height: 15px;
	margin-right: 15px;
}
</style>

<script>
import { UserNotificationType } from "@/enums/UserNotificationType"
import { eventBus } from "@/eventBus"
import { EventBusEvents } from "@/enums/EventBusEvents"
import { NotificationType } from "@/enums/NotificationType"
import loaderMixin from "@/mixins/loader-mixin"
import phoneFilterMixin from "@/mixins/phone-filter-mixin"
import SkRadio from "@/components/common/SkRadio.vue"
import SkToggle from "@/components/common/SkToggle.vue"
import NewCreditCardModal from "@/components/modals/NewCreditCardModal.vue"
import EditUserAccountModal from "@/components/modals/EditUserAccountModal.vue"

export default {
	name: "UserAccountSettings",
	mixins: [loaderMixin, phoneFilterMixin],
	components: {
		SkRadio,
		SkToggle,
		NewCreditCardModal,
		EditUserAccountModal
	},
	computed: {
		user() {
			return this.$store.state.user
		},
		hasApartmentNumber() {
			return (
				this.$store.state.user &&
				this.$store.state.user.apartmentNumber.length > 0
			)
		},
		creditCards() {
			return this.$store.state.user.paymentMethods
				? this.$store.state.user.paymentMethods
				: []
		},
		defaultCardId: {
			get: function () {
				const defaultCard = this.creditCards.find(
					(card) => card.is_default == 1
				)
				return defaultCard ? defaultCard.id : null
			},
			set: function (id) {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Set Default Payment Method Clicked",
					data: {
						payment_method_id: id
					}
				})
				this.toggleLoader(true)
				this.$store
					.dispatch("setDefaultPaymentMethod", id)
					.finally(() => {
						this.toggleLoader(false)
					})
			}
		},
		optedIntoDailySMS: {
			get: function () {
				return this.user.smsOptIn
			},
			set: function (optIn) {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: optIn ? "SMS Opt In" : "SMS Opt Out"
				})
				this.toggleLoader(true)
				this.$store
					.dispatch("updateUser", {
						smsOptIn: optIn ? 1 : 0
					})
					.then(() => {
						let message = optIn
							? "You're now subscribed to daily menu SMS updates!"
							: "You've been unsubcribed from daily menu SMS updates"
						eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
							message: message,
							type: NotificationType.SUCCESS
						})
					})
					.catch(() => {
						eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
							message: "An error occurred, please try again.",
							type: NotificationType.ERROR
						})
					})
					.finally(() => {
						this.toggleLoader(false)
					})
			}
		},
		optedIntoEventReminders: {
			get: function () {
				return (
					this.$store.state.user.notifications &&
					this.$store.state.user.notifications.find(
						(notification) =>
							notification.type ==
							UserNotificationType.UPCOMING_EVENT_SMS
					) != undefined
				)
			},
			set: function (optIn) {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Event Reminder Toggled",
					data: {
						opt_in: optIn
					}
				})
				this.toggleLoader(true)
				this.$store
					.dispatch("updateUserNotification", {
						type: UserNotificationType.UPCOMING_EVENT_SMS,
						optIn: optIn
					})
					.then(() => {
						let message = optIn
							? "You will receive a notification when a new event is available"
							: "You've been unsubcribed from event updates"
						eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
							message: message,
							type: NotificationType.SUCCESS
						})
					})
					.catch(() => {
						eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
							message: "An error occurred, please try again.",
							type: NotificationType.ERROR
						})
					})
					.finally(() => {
						this.toggleLoader(false)
					})
			}
		}
	},
	mounted: function () {
		if (this.$store.getters.isLoggedIn) {
			this.$store.dispatch("getUser")
			this.$store.dispatch("getUserPaymentMethods")
		}
	},
	methods: {
		editUserAccountClicked() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Edit User Account Clicked"
			})
			this.$refs.editUserAccountModal.open()
		},
		newCreditCardClicked() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "New Credit Card Clicked"
			})
			this.$refs.newCreditCardModal.open()
		},
		async deleteCardClicked(card) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Delete Credit Card Clicked"
			})
			this.toggleLoader(true)
			this.$store.dispatch("deletePaymentMethod", card.id).finally(() => {
				this.toggleLoader(false)
			})
		}
	}
}
</script>